import { useState } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { duplicatePathApi } from './UnderwritePath/pathApi';
import { Box } from '@mui/material';

interface Props {
    path: {
        pathId?: number;
    };
    onSuccess?: () => void;
    refetch?: () => Promise<any>;
}

export const DuplicateButton = ({ path, onSuccess, refetch }: Props) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [successOpen, setSuccessOpen] = useState(false);
    const [errorOpen, setErrorOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const handleDuplicate = async () => {
        if (!path.pathId) {
            setErrorMessage('Invalid Path Id');
            setErrorOpen(true);
            return;
        }

        try {
            setLoading(true);
            await duplicatePathApi(path.pathId);
            setOpen(false);
            setSuccessOpen(true);
             if (refetch) {
                await refetch();
            }

            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            setErrorMessage('Failed to duplicate path');
            setErrorOpen(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Button variant="contained" color="secondary" onClick={() => setOpen(true)}>
                Duplicate Path
            </Button>
            <Dialog
                open={open}
                onClose={() => {
                    if (!loading) {
                        setOpen(false);
                    }
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Duplicate Path</DialogTitle>

                <DialogContent>
                    <DialogContentText component="div">Are you sure you want to duplicate this path?</DialogContentText>
                    <Box component="ul" sx={{ mt: 1, pl: 2 }}></Box>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={loading}>
                        Cancel
                    </Button>

                    <Button variant="contained" color="secondary" onClick={handleDuplicate} disabled={loading}>
                        {loading ? <CircularProgress size={20} color="inherit" /> : 'Duplicate'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={successOpen}
                autoHideDuration={3000}
                onClose={() => setSuccessOpen(false)}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Alert severity="success" onClose={() => setSuccessOpen(false)} variant="filled">
                    Path duplicated successfully
                </Alert>
            </Snackbar>

            <Snackbar
                open={errorOpen}
                autoHideDuration={4000}
                onClose={() => setErrorOpen(false)}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Alert severity="error" onClose={() => setErrorOpen(false)} variant="filled">
                    {errorMessage}
                </Alert>
            </Snackbar>
        </>
    );
};
